import { useEffect, useState } from 'react';

import { getServerTime } from 'services/dashboard.service';

// Offset servidor-cliente memorizado por sessão (escopo de módulo): a PRIMEIRA montagem
// da sessão paga a chamada de rede; as seguintes (troca de aba MAD⇄Diretoria, volta pelo
// guarda-chuva) resolvem sincronamente — sem rede e sem tela de loading.
let cachedServerOffset: number | null = null;

/** Só para testes: cada teste começa sem a hora do servidor memorizada. */
export function resetServerTimeCache() {
  cachedServerOffset = null;
}

interface UseServerTimeOptions {
  /** Avança o relógio a cada segundo (contagens regressivas). Sem ele, a hora fica congelada na montagem. */
  tick?: boolean;
}

/**
 * Hora do SERVIDOR. Sem `tick`, congelada na montagem (o comportamento do ApreciarRealizacao,
 * de onde o hook veio). Com `tick`, avança 1 s por segundo a partir da hora do servidor.
 *
 * `hasTime` só fica verdadeiro com a hora do servidor em mãos: antes disso, `realDatetime` é a
 * do navegador e não deve decidir prazo nenhum.
 */
export function useServerTime({ tick = false }: UseServerTimeOptions = {}) {
  const [realDatetime, setRealDatetime] = useState<number>(() =>
    cachedServerOffset !== null ? Date.now() + cachedServerOffset : 0,
  );
  const [hasTime, setHasTime] = useState(() => cachedServerOffset !== null);

  useEffect(() => {
    let ativo = true;
    let intervalo: number | undefined;
    const comecarTique = () => {
      if (tick) intervalo = window.setInterval(() => setRealDatetime(anterior => anterior + 1000), 1000);
    };

    if (cachedServerOffset !== null) {
      comecarTique();
    } else {
      getServerTime().then(timestamp => {
        cachedServerOffset = timestamp - Date.now();
        // Se a página saiu antes da resposta, nada de estado nem de temporizador órfão.
        if (!ativo) return;
        setRealDatetime(timestamp);
        setHasTime(true);
        comecarTique();
      });
    }

    return () => {
      ativo = false;
      window.clearInterval(intervalo);
    };
  }, [tick]);

  return { realDatetime, hasTime };
}
